import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { authFetch, authJsonFetch, getAuthHeaders } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Upload, 
  Download, 
  Trash2,
  FileText,
  Image,
  File,
  Loader2,
  Link as LinkIcon,
  ExternalLink,
  Plus,
  Layers,
  Camera,
  CreditCard,
  Pencil,
  Check,
  X,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { OrderMergeModal } from '../components/OrderMergeModal';
import { PhotographersModal } from '../components/PhotographersModal';
import { CFCardsModal } from '../components/CFCardsModal';
import { HelpButton } from '../components/help';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

const categories = [
  { value: 'documentation', label: 'Documentation' },
  { value: 'template', label: 'Template' },
  { value: 'asset', label: 'Asset' },
  { value: 'link', label: 'Link' },
  { value: 'other', label: 'Other' },
];

export const ResourcesPage = () => {
  const { isBarbiePink } = useTheme();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [addingLink, setAddingLink] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [uploadMode, setUploadMode] = useState('file'); // 'file' or 'link'
  const [orderMergeOpen, setOrderMergeOpen] = useState(false);
  const [photographersOpen, setPhotographersOpen] = useState(false);
  const [cfCardsOpen, setCFCardsOpen] = useState(false);
  const [templateUploadOpen, setTemplateUploadOpen] = useState(false);
  const [documentTemplates, setDocumentTemplates] = useState([]);
  const [uploadingTemplate, setUploadingTemplate] = useState(false);
  const [templateForm, setTemplateForm] = useState({ name: '', description: '' });
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [syncingTemplate, setSyncingTemplate] = useState(null);
  const [showPlaceholders, setShowPlaceholders] = useState(false);
  const [placeholders, setPlaceholders] = useState(null);
  const [placeholderYear, setPlaceholderYear] = useState(new Date().getFullYear());
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [deletingTemplates, setDeletingTemplates] = useState(false);
  const [renamingTemplate, setRenamingTemplate] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [editingDescriptionId, setEditingDescriptionId] = useState(null);
  const [editingDescriptionValue, setEditingDescriptionValue] = useState('');
  const [templatesCollapsed, setTemplatesCollapsed] = useState(false);
  const templateInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const [uploadForm, setUploadForm] = useState({
    category: 'other',
    description: ''
  });
  const [linkForm, setLinkForm] = useState({
    name: '',
    url: '',
    category: 'link',
    description: ''
  });

  useEffect(() => {
    fetchResources();
    fetchDocumentTemplates();
  }, [categoryFilter]);

  const fetchResources = async () => {
    try {
      const params = new URLSearchParams();
      if (categoryFilter && categoryFilter !== 'all') params.append('category', categoryFilter);

      const response = await authFetch(`${API}/resources?${params}`);
      if (response.ok) {
        const data = await response.json();
        setResources(data);
      }
    } catch (err) {
      console.error('Failed to fetch resources:', err);
      toast.error('Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentTemplates = async () => {
    try {
      const response = await authFetch(`${API}/documents/templates`);
      if (response.ok) {
        const data = await response.json();
        setDocumentTemplates(data.templates || []);
      }
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    }
  };

  const handleTemplateUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    // Filter for valid .docx files
    const validFiles = Array.from(files).filter(f => f.name.toLowerCase().endsWith('.docx'));
    if (validFiles.length === 0) {
      toast.error('Only .docx files are allowed');
      return;
    }

    setUploadingTemplate(true);
    
    try {
      if (validFiles.length === 1) {
        // Single file upload
        const file = validFiles[0];
        const formData = new FormData();
        formData.append('file', file);
        // Use provided name or fall back to filename
        const templateName = templateForm.name.trim() || file.name.replace('.docx', '');
        formData.append('name', templateName);
        formData.append('description', templateForm.description.trim());

        const token = localStorage.getItem('gs_session_token');
        const response = await fetch(`${API}/documents/templates/upload`, {
          method: 'POST',
          credentials: 'include',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          body: formData
        });

        if (response.ok) {
          toast.success('Template uploaded successfully');
          fetchDocumentTemplates();
          setTemplateForm({ name: '', description: '' });
          setTemplateUploadOpen(false);
        } else {
          const err = await response.json().catch(() => ({}));
          toast.error(err.detail || 'Failed to upload template');
        }
      } else {
        // Bulk upload
        const formData = new FormData();
        validFiles.forEach(file => {
          formData.append('files', file);
        });

        const token = localStorage.getItem('gs_session_token');
        const response = await fetch(`${API}/documents/templates/upload-bulk`, {
          method: 'POST',
          credentials: 'include',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          body: formData
        });

        if (response.ok) {
          const data = await response.json();
          if (data.uploaded?.length > 0) {
            toast.success(`${data.uploaded.length} template(s) uploaded successfully`);
          }
          if (data.errors?.length > 0) {
            toast.error(`${data.errors.length} file(s) failed to upload`);
          }
          fetchDocumentTemplates();
          setTemplateForm({ name: '', description: '' });
          setTemplateUploadOpen(false);
        } else {
          const err = await response.json().catch(() => ({}));
          toast.error(err.detail || 'Failed to upload templates');
        }
      }
    } catch (err) {
      console.error('Upload exception:', err);
      toast.error('An error occurred during upload');
    } finally {
      setUploadingTemplate(false);
      if (templateInputRef.current) templateInputRef.current.value = '';
    }
  };

  // Edit template description
  const handleEditDescription = async (templateId, newDescription) => {
    try {
      const formData = new FormData();
      formData.append('description', newDescription);
      
      const token = localStorage.getItem('gs_session_token');
      const response = await fetch(`${API}/documents/templates/${templateId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData
      });

      if (response.ok) {
        toast.success('Description updated');
        fetchDocumentTemplates();
        setEditingDescriptionId(null);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to update description');
      }
    } catch (err) {
      console.error('Update error:', err);
      toast.error('Failed to update description');
    }
  };

  const deleteTemplate = async (templateId) => {
    console.log('deleteTemplate called with:', templateId);
    
    try {
      const response = await authFetch(`${API}/documents/templates/${templateId}`, {
        method: 'DELETE'
      });
      
      console.log('Delete response:', response.status);
      
      if (response.ok) {
        toast.success('Template deleted successfully');
        fetchDocumentTemplates();
      } else {
        const err = await response.json().catch(() => ({}));
        console.error('Delete error response:', err);
        toast.error(err.detail || 'Failed to delete template');
      }
    } catch (err) {
      console.error('Delete exception:', err);
      toast.error('An error occurred while deleting');
    }
  };

  // Edit template in Google Docs
  const editInGoogleDocs = async (templateId) => {
    setEditingTemplate(templateId);
    try {
      const response = await authFetch(`${API}/documents/templates/${templateId}/edit-in-google-docs`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        // Open Google Docs in a new tab
        window.open(data.edit_url, '_blank');
        toast.success(data.message || 'Opened in Google Docs');
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to open in Google Docs');
      }
    } catch (err) {
      console.error('Edit in Google Docs error:', err);
      toast.error('Failed to open in Google Docs. Make sure Google Drive is connected.');
    } finally {
      setEditingTemplate(null);
    }
  };

  // Sync template from Google Docs
  const syncFromGoogleDocs = async (templateId) => {
    setSyncingTemplate(templateId);
    try {
      const response = await authFetch(`${API}/documents/templates/${templateId}/sync-from-google-docs`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Template synced successfully');
        fetchDocumentTemplates(); // Refresh the list
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to sync template');
      }
    } catch (err) {
      console.error('Sync from Google Docs error:', err);
      toast.error('Failed to sync template');
    } finally {
      setSyncingTemplate(null);
    }
  };

  // Handle bulk delete of templates
  const handleBulkDelete = async () => {
    if (selectedTemplates.length === 0) return;
    
    const hasDefault = selectedTemplates.some(id => 
      documentTemplates.find(t => t.id === id && !t.is_custom)
    );
    const confirmMsg = hasDefault 
      ? `Are you sure you want to delete ${selectedTemplates.length} template(s)? This includes DEFAULT templates which will be permanently removed. This action cannot be undone.`
      : `Are you sure you want to delete ${selectedTemplates.length} template(s)? This action cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;
    
    setDeletingTemplates(true);
    try {
      const response = await authJsonFetch(`${API}/documents/templates/bulk-delete`, {
        method: 'POST',
        body: JSON.stringify({ template_ids: selectedTemplates })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || `Deleted ${data.deleted_count} template(s)`);
        setSelectedTemplates([]);
        fetchDocumentTemplates();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to delete templates');
      }
    } catch (err) {
      console.error('Bulk delete error:', err);
      toast.error('An error occurred while deleting templates');
    } finally {
      setDeletingTemplates(false);
    }
  };

  // Handle template selection toggle
  const handleTemplateSelect = (templateId, checked) => {
    if (checked) {
      setSelectedTemplates(prev => [...prev, templateId]);
    } else {
      setSelectedTemplates(prev => prev.filter(id => id !== templateId));
    }
  };

  // Handle select all templates (includes all templates for deletion)
  const handleSelectAllTemplates = (checked) => {
    if (checked) {
      const allIds = documentTemplates.map(t => t.id);
      setSelectedTemplates(allIds);
    } else {
      setSelectedTemplates([]);
    }
  };

  // Start renaming a template
  const startRename = (template) => {
    setRenamingTemplate(template.id);
    setRenameValue(template.name);
  };

  // Cancel rename
  const cancelRename = () => {
    setRenamingTemplate(null);
    setRenameValue('');
  };

  // Save renamed template
  const saveRename = async (templateId) => {
    if (!renameValue.trim()) {
      toast.error('Template name cannot be empty');
      return;
    }
    
    try {
      const token = localStorage.getItem('gs_session_token');
      const response = await fetch(
        `${API}/documents/templates/${templateId}/rename`,
        { 
          method: 'PUT',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify({ new_name: renameValue.trim() })
        }
      );
      
      if (response.ok) {
        toast.success('Template renamed successfully');
        setRenamingTemplate(null);
        setRenameValue('');
        fetchDocumentTemplates();
      } else {
        const err = await response.json().catch(() => ({}));
        console.error('Rename error response:', err);
        toast.error(err.detail || 'Failed to rename template');
      }
    } catch (err) {
      console.error('Rename error:', err);
      toast.error('An error occurred while renaming');
    }
  };

  // Fetch available placeholders
  const fetchPlaceholders = async (year = placeholderYear) => {
    try {
      const response = await authFetch(`${API}/documents/templates/placeholders?year=${year}`);
      if (response.ok) {
        const data = await response.json();
        setPlaceholders(data.placeholders);
      }
    } catch (err) {
      console.error('Failed to fetch placeholders:', err);
    }
  };

  // Handle placeholder year change
  const handlePlaceholderYearChange = (year) => {
    setPlaceholderYear(year);
    fetchPlaceholders(year);
  };

  // Toggle placeholders panel
  const togglePlaceholders = () => {
    if (!showPlaceholders && !placeholders) {
      fetchPlaceholders();
    }
    setShowPlaceholders(!showPlaceholders);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', uploadForm.category);
      formData.append('description', uploadForm.description);

      const response = await authFetch(`${API}/resources`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        toast.success('File uploaded successfully');
        fetchResources();
        setUploadForm({ category: 'other', description: '' });
      } else {
        toast.error('Failed to upload file');
      }
    } catch (err) {
      toast.error('An error occurred');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleAddLink = async (e) => {
    e.preventDefault();
    if (!linkForm.name.trim() || !linkForm.url.trim()) {
      toast.error('Please enter both name and URL');
      return;
    }

    // Basic URL validation
    let url = linkForm.url.trim();
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }

    setAddingLink(true);
    try {
      const response = await authJsonFetch(`${API}/resources/link`, {
        method: 'POST',
        body: JSON.stringify({
          name: linkForm.name.trim(),
          url: url,
          category: linkForm.category,
          description: linkForm.description
        })
      });

      if (response.ok) {
        toast.success('Link added successfully');
        fetchResources();
        setLinkForm({ name: '', url: '', category: 'link', description: '' });
      } else {
        toast.error('Failed to add link');
      }
    } catch (err) {
      toast.error('An error occurred');
    } finally {
      setAddingLink(false);
    }
  };

  const handleDownload = async (resource) => {
    // For URL links, open in new tab
    if (resource.storage_type === 'url_link' || resource.mime_type === 'text/x-uri') {
      window.open(resource.url || resource.file_path, '_blank', 'noopener,noreferrer');
      return;
    }

    try {
      const response = await authFetch(`${API}/resources/${resource.resource_id}/download`);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = resource.original_filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        toast.error('Failed to download file');
      }
    } catch (err) {
      toast.error('Download error');
    }
  };

  const handleDelete = async (resourceId) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) return;
    
    try {
      const response = await authFetch(`${API}/resources/${resourceId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Resource deleted');
        fetchResources();
      } else {
        toast.error('Failed to delete resource');
      }
    } catch (err) {
      toast.error('An error occurred');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (resource) => {
    if (resource.storage_type === 'url_link' || resource.mime_type === 'text/x-uri') return LinkIcon;
    const mimeType = resource.mime_type;
    if (mimeType?.startsWith('image/')) return Image;
    if (mimeType?.includes('pdf') || mimeType?.includes('document')) return FileText;
    return File;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: 'Australia/Sydney'
    });
  };

  return (
    <div data-testid="resources-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Resource Library</h1>
          <p className="text-muted-foreground mt-1">
            Shared repository for documentation and assets
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setPhotographersOpen(true)}
            variant="outline"
            className={cn("flex items-center gap-2", isBarbiePink && "border-pink-500")}
            data-testid="photographers-btn"
          >
            <Camera className="w-4 h-4" />
            Photographers
          </Button>
          <Button
            onClick={() => setCFCardsOpen(true)}
            variant="outline"
            className={cn("flex items-center gap-2", isBarbiePink && "border-pink-500")}
            data-testid="cf-cards-btn"
          >
            <CreditCard className="w-4 h-4" />
            CF Cards
          </Button>
          <Button
            onClick={() => setOrderMergeOpen(true)}
            className={cn("flex items-center gap-2", isBarbiePink && "bg-pink-500 hover:bg-pink-600")}
            data-testid="merge-orders-btn"
          >
            <Layers className="w-4 h-4" />
            MergeOrders
          </Button>
        </div>
      </div>

      {/* OrderMerge Modal */}
      <OrderMergeModal 
        open={orderMergeOpen} 
        onClose={() => setOrderMergeOpen(false)} 
        isBarbiePink={isBarbiePink}
      />

      {/* Photographers Modal */}
      <PhotographersModal
        open={photographersOpen}
        onClose={() => setPhotographersOpen(false)}
        isAdmin={isAdmin}
        isBarbiePink={isBarbiePink}
      />

      {/* CF Cards Modal */}
      <CFCardsModal
        open={cfCardsOpen}
        onClose={() => setCFCardsOpen(false)}
        isAdmin={isAdmin}
        isBarbiePink={isBarbiePink}
      />

      {/* Document Templates Section */}
      <Card className={cn(isBarbiePink && "card-brutalist")}>
        <CardHeader 
          className="cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => setTemplatesCollapsed(!templatesCollapsed)}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              {templatesCollapsed ? (
                <ChevronDown className="w-5 h-5 text-muted-foreground" />
              ) : (
                <ChevronUp className="w-5 h-5 text-muted-foreground" />
              )}
              <FileText className="w-5 h-5 text-primary" />
              Document Templates
              <span className="text-sm font-normal text-muted-foreground">
                ({documentTemplates.length})
              </span>
            </CardTitle>
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              {/* Bulk Delete Button - shown when templates selected */}
              {isAdmin && selectedTemplates.length > 0 && (
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={handleBulkDelete}
                  disabled={deletingTemplates}
                  data-testid="bulk-delete-templates-btn"
                >
                  {deletingTemplates ? (
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  ) : (
                    <Trash2 className="w-3 h-3 mr-1" />
                  )}
                  Delete Selected ({selectedTemplates.length})
                </Button>
              )}
              <HelpButton topic="document_templates" />
              <Button
                size="sm"
                variant="outline"
                onClick={togglePlaceholders}
                data-testid="show-placeholders-btn"
              >
                {showPlaceholders ? 'Hide' : 'Show'} Placeholders
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setTemplateUploadOpen(!templateUploadOpen)}
              >
                <Upload className="w-3 h-3 mr-1" />
                Upload Template
              </Button>
            </div>
          </div>
        </CardHeader>
        {!templatesCollapsed && (
        <CardContent>
          {/* Placeholders Reference Panel */}
          {showPlaceholders && placeholders && (
            <div className="mb-4 p-4 border rounded-lg bg-blue-50 dark:bg-blue-950 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-sm flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Available Merge Field Placeholders
                </h4>
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Year:</Label>
                  <Select 
                    value={String(placeholderYear)} 
                    onValueChange={(v) => handlePlaceholderYearChange(Number(v))}
                  >
                    <SelectTrigger className="w-24 h-7 text-xs" data-testid="placeholder-year-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026, 2027, 2028].map(y => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Copy these placeholders into your Word document. Select the year to match your document generation settings.
              </p>
              <div className="space-y-4 max-h-[400px] overflow-y-auto">
                {Object.entries(placeholders).map(([categoryKey, fields]) => (
                  <div key={categoryKey} className="space-y-2">
                    <h5 className="font-medium text-sm capitalize">{categoryKey.replace(/_/g, ' ')}</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                      {Array.isArray(fields) && fields.map((field, idx) => (
                        <div 
                          key={idx} 
                          className="text-xs p-2 bg-white dark:bg-gray-800 rounded border cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                          onClick={() => {
                            const placeholder = `« ${field.key} »`;
                            navigator.clipboard.writeText(placeholder);
                            toast.success(`Copied: ${placeholder}`);
                          }}
                        >
                          <code className="text-blue-600 dark:text-blue-400">« {field.key} »</code>
                          <span className="text-muted-foreground ml-2">- {field.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Form */}
          {templateUploadOpen && (
            <div className="mb-4 p-4 border rounded-lg bg-muted/30 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Template Name (optional for bulk upload)</Label>
                  <Input
                    placeholder="e.g., High School Confirmation (or use filename)"
                    value={templateForm.name}
                    onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground mt-1">Leave blank to use filename as name</p>
                </div>
                <div>
                  <Label className="text-sm">Description</Label>
                  <Input
                    placeholder="Brief description..."
                    value={templateForm.description}
                    onChange={(e) => setTemplateForm(prev => ({ ...prev, description: e.target.value }))}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  ref={templateInputRef}
                  onChange={handleTemplateUpload}
                  accept=".docx"
                  multiple
                  className="hidden"
                />
                <Button
                  size="sm"
                  onClick={() => templateInputRef.current?.click()}
                  disabled={uploadingTemplate}
                >
                  {uploadingTemplate ? (
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  ) : (
                    <Upload className="w-3 h-3 mr-1" />
                  )}
                  Select .docx File(s)
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setTemplateUploadOpen(false);
                    setTemplateForm({ name: '', description: '' });
                  }}
                >
                  Cancel
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Upload Word documents (.docx) with merge fields. Select multiple files for bulk upload - names will be taken from filenames.
              </p>
            </div>
          )}

          {/* Template List */}
          <div className="space-y-2">
            {documentTemplates.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No templates available</p>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      {isAdmin && (
                        <TableHead className="w-10">
                          <Checkbox
                            checked={selectedTemplates.length > 0 && selectedTemplates.length === documentTemplates.length}
                            onCheckedChange={handleSelectAllTemplates}
                            data-testid="select-all-templates"
                            title="Select all templates"
                          />
                        </TableHead>
                      )}
                      <TableHead>Template Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-center">Type</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {documentTemplates.map((template) => (
                      <TableRow key={template.id} data-testid={`template-row-${template.id}`}>
                        {isAdmin && (
                          <TableCell>
                            <Checkbox
                              checked={selectedTemplates.includes(template.id)}
                              onCheckedChange={(checked) => handleTemplateSelect(template.id, checked)}
                              data-testid={`select-template-${template.id}`}
                            />
                          </TableCell>
                        )}
                        <TableCell className="font-medium">
                          {renamingTemplate === template.id ? (
                            <div className="flex items-center gap-2">
                              <Input
                                value={renameValue}
                                onChange={(e) => setRenameValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveRename(template.id);
                                  if (e.key === 'Escape') cancelRename();
                                }}
                                className="h-8 w-48"
                                autoFocus
                                data-testid={`rename-input-${template.id}`}
                              />
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => saveRename(template.id)}
                                className="h-8 w-8 p-0"
                                data-testid={`save-rename-${template.id}`}
                              >
                                <Check className="w-4 h-4 text-green-600" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={cancelRename}
                                className="h-8 w-8 p-0"
                                data-testid={`cancel-rename-${template.id}`}
                              >
                                <X className="w-4 h-4 text-red-600" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span>{template.name}</span>
                              {template.synced_from_google_docs && (
                                <Badge variant="outline" className="text-xs">Synced</Badge>
                              )}
                              {isAdmin && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => startRename(template)}
                                  className="h-6 w-6 p-0 opacity-50 hover:opacity-100"
                                  data-testid={`rename-template-${template.id}`}
                                  title="Rename template"
                                >
                                  <Pencil className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {editingDescriptionId === template.id ? (
                            <div className="flex items-center gap-2">
                              <Input
                                value={editingDescriptionValue}
                                onChange={(e) => setEditingDescriptionValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleEditDescription(template.id, editingDescriptionValue);
                                  }
                                  if (e.key === 'Escape') {
                                    setEditingDescriptionId(null);
                                  }
                                }}
                                placeholder="Enter description..."
                                className="h-7 text-sm"
                                autoFocus
                              />
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleEditDescription(template.id, editingDescriptionValue)}
                                className="h-7 w-7 p-0"
                              >
                                <Check className="w-3 h-3 text-green-600" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setEditingDescriptionId(null)}
                                className="h-7 w-7 p-0"
                              >
                                <X className="w-3 h-3 text-red-600" />
                              </Button>
                            </div>
                          ) : (
                            <div 
                              className="flex items-center gap-1 group cursor-pointer"
                              onClick={() => {
                                if (isAdmin) {
                                  setEditingDescriptionId(template.id);
                                  setEditingDescriptionValue(template.description || '');
                                }
                              }}
                              title={isAdmin ? "Click to edit description" : undefined}
                            >
                              <span>{template.description || '-'}</span>
                              {isAdmin && (
                                <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-50" />
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant={template.is_custom ? "secondary" : "outline"}>
                            {template.is_custom ? "Custom" : "Default"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {/* Edit in Google Docs */}
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => editInGoogleDocs(template.id)}
                              disabled={editingTemplate === template.id}
                              data-testid={`edit-template-${template.id}`}
                              title="Edit in Google Docs. After saving in Drive, click Sync to pull your changes back."
                            >
                              {editingTemplate === template.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <ExternalLink className="w-3 h-3" />
                              )}
                              <span className="ml-1 hidden sm:inline">Edit in Drive</span>
                            </Button>
                            
                            {/* Sync from Google Docs */}
                            <Button
                              type="button"
                              size="sm"
                              variant={template.has_pending_drive_edits ? "default" : "outline"}
                              onClick={() => syncFromGoogleDocs(template.id)}
                              disabled={syncingTemplate === template.id}
                              data-testid={`sync-template-${template.id}`}
                              title="Pull saved changes from Google Docs back into this template"
                              className={template.has_pending_drive_edits ? "bg-amber-500 hover:bg-amber-600 text-white" : ""}
                            >
                              {syncingTemplate === template.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Download className="w-3 h-3" />
                              )}
                              <span className="ml-1 hidden sm:inline">{template.has_pending_drive_edits ? 'Pull Edits' : 'Sync'}</span>
                            </Button>
                            
                            {/* Delete (admin only - works for both custom and default) */}
                            {isAdmin && (
                              <Button
                                type="button"
                                size="sm"
                                variant="destructive"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const warningMsg = template.is_custom 
                                    ? `Delete template "${template.name}"?`
                                    : `Delete DEFAULT template "${template.name}"? This will permanently remove it from the system.`;
                                  if (window.confirm(warningMsg)) {
                                    deleteTemplate(template.id);
                                  }
                                }}
                                data-testid={`delete-template-${template.id}`}
                                title={template.is_custom ? "Delete template" : "Delete default template"}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="text-xs text-muted-foreground mt-2 space-y-1">
                  <p><strong>Edit:</strong> Opens the template in Google Docs for editing. Add/modify placeholders as needed.</p>
                  <p><strong>Sync:</strong> Saves your Google Docs changes back to the system. For default templates, creates a custom copy.</p>
                </div>
              </>
            )}
          </div>
        </CardContent>
        )}
      </Card>

      {/* Upload Section */}
      <Card className={cn(isBarbiePink && "card-brutalist")}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-primary" />
            Add Resource
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Toggle between File and Link */}
          <div className="flex gap-2 mb-4">
            <Button
              variant={uploadMode === 'file' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setUploadMode('file')}
              data-testid="upload-mode-file"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload File
            </Button>
            <Button
              variant={uploadMode === 'link' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setUploadMode('link')}
              data-testid="upload-mode-link"
            >
              <LinkIcon className="w-4 h-4 mr-2" />
              Add URL Link
            </Button>
          </div>

          {uploadMode === 'file' ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Category</Label>
                <Select 
                  value={uploadForm.category} 
                  onValueChange={(v) => setUploadForm({...uploadForm, category: v})}
                >
                  <SelectTrigger data-testid="upload-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.filter(c => c.value !== 'link').map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  data-testid="upload-description-input"
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
                  placeholder="Optional description"
                />
              </div>
              <div className="flex items-end">
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleUpload}
                  className="hidden"
                  id="file-upload"
                />
                <Button
                  data-testid="upload-file-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-full"
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  {uploading ? 'Uploading...' : 'Choose File'}
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleAddLink} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label>Name *</Label>
                <Input
                  data-testid="link-name-input"
                  value={linkForm.name}
                  onChange={(e) => setLinkForm({...linkForm, name: e.target.value})}
                  placeholder="e.g., Company Website"
                  required
                />
              </div>
              <div>
                <Label>URL *</Label>
                <Input
                  data-testid="link-url-input"
                  value={linkForm.url}
                  onChange={(e) => setLinkForm({...linkForm, url: e.target.value})}
                  placeholder="https://example.com"
                  required
                />
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  data-testid="link-description-input"
                  value={linkForm.description}
                  onChange={(e) => setLinkForm({...linkForm, description: e.target.value})}
                  placeholder="Optional description"
                />
              </div>
              <div className="flex items-end">
                <Button
                  type="submit"
                  data-testid="add-link-btn"
                  disabled={addingLink}
                  className="w-full"
                >
                  {addingLink ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4 mr-2" />
                  )}
                  {addingLink ? 'Adding...' : 'Add Link'}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      {/* Filter */}
      <Card className={cn(isBarbiePink && "card-brutalist")}>
        <CardContent className="p-4">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger data-testid="resource-category-filter" className="w-full sm:w-64">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(c => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Resources Table */}
      <Card className={cn(isBarbiePink && "card-brutalist")}>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>File</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="hidden md:table-cell">Size</TableHead>
                <TableHead className="hidden lg:table-cell">Uploaded</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : resources.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    No resources found
                  </TableCell>
                </TableRow>
              ) : (
                resources.map((resource) => {
                  const FileIcon = getFileIcon(resource);
                  const isLink = resource.storage_type === 'url_link' || resource.mime_type === 'text/x-uri';
                  return (
                    <TableRow key={resource.resource_id} data-testid={`resource-row-${resource.resource_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            "p-2 rounded-lg",
                            isBarbiePink ? "bg-pink-500/20" : "bg-blue-500/10"
                          )}>
                            <FileIcon className="w-4 h-4 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">{resource.original_filename}</p>
                            {resource.description && (
                              <p className="text-sm text-muted-foreground line-clamp-1">
                                {resource.description}
                              </p>
                            )}
                            {isLink && resource.url && (
                              <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                                {resource.url}
                              </p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {resource.category}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {isLink ? '-' : formatFileSize(resource.file_size)}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-muted-foreground">
                        {formatDate(resource.uploaded_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            data-testid={`download-resource-${resource.resource_id}`}
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDownload(resource)}
                            title={isLink ? 'Open Link' : 'Download'}
                          >
                            {isLink ? <ExternalLink className="w-4 h-4" /> : <Download className="w-4 h-4" />}
                          </Button>
                          <Button
                            data-testid={`delete-resource-${resource.resource_id}`}
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDelete(resource.resource_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
